/**
 * @file routePlanning.ts
 * @module api/apiJSON/routePlanning
 * @description Formats route planning request payloads for create, summary,
 * list, and vehicle/order status APIs in the admin app.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import {
  ICreatePlan,
  IListPlanSummary,
  IListPlans,
  IRouteAddOrder,
  IVariantVehicleStatusUpdate,
  IVehicleOrderStatusUpdate,
  IVehicleOrderSubmitPartialPayment,
  IVehicleRouteStatusUpdate,
} from '../../types';
import Method from '../../utils/methods';

export const routePlanningJSON = {
  listPlanSummary: ({
    date,
    orders,
    route,
    warehouse,
    vehicles,
    enforceMaxOrdersPerVehicleLimit, // Optional boolean to enforce max orders per vehicle limit
    planSummaryContext, // Optional string to provide context for the plan summary
  }: IListPlanSummary) => {
    const params: any = {
      date: date,
      orders: orders,
      route: route,
      vehicles: vehicles,
    };
    if (typeof enforceMaxOrdersPerVehicleLimit === 'boolean') {
      params.enforceMaxOrdersPerVehicleLimit = enforceMaxOrdersPerVehicleLimit;
    }
    if (typeof planSummaryContext === 'string' && planSummaryContext.trim()) {
      params.planSummaryContext = planSummaryContext;
    }
    if (warehouse) {
      params.warehouse = warehouse;
    }
    return params;
  },
  createPlan: ({ date, orders, vehicles, planCreateContext }: ICreatePlan) => {
    const params: any = {
      date: date,
      orders: orders,
      vehicles: vehicles,
    };
    if (typeof planCreateContext === 'string' && planCreateContext.trim()) {
      params.planCreateContext = planCreateContext;
    }
    return params;
  },
  listPlan: ({
    limit,
    pageNo,
    sortKey,
    sortOrder,
    fromDate,
    needCount,
    toDate,
    status,
  }: IListPlans) => {
    return {
      limit: limit,
      pageNo: pageNo,
      sortKey: sortKey,
      sortOrder: sortOrder,
      fromDate: fromDate,
      needCount: needCount,
      toDate: toDate,
      status: status,
    };
  },
  variantVehicleStatusUpdate: ({
    order,
    variant,
    status,
    quantityType,
  }: IVariantVehicleStatusUpdate) => {
    return {
      order: order,
      variant: variant,
      status: status,
      quantityType: quantityType,
    };
  },
  vehicleRouteStatusUpdate: ({
    status,
    cancellationReason,
    cancellationNote,
  }: IVehicleRouteStatusUpdate) => ({
    status,
    ...(cancellationReason && { cancellationReason }),
    ...(cancellationNote && { cancellationNote }),
  }),
  markGoodsAsLoadedUpdate: (image: string) => {
    let form = new FormData();
    if (typeof image === 'string' && image.startsWith('data:')) {
      const binaryImage = Method.dataURLtoBlob(image);
      const file = new File([binaryImage], 'captured_image.png', {
        type: binaryImage.type,
      });
      form.append('image', file);
    } else if (image) {
      form.append('image', image);
    }
    return form;
  },
  vehicleOrderStatusUpdate: ({
    order,
    paymentMode,
    status,
    collectedCharge,
    scheduleForNextDay,
  }: IVehicleOrderStatusUpdate) => {
    let temp: any = {
      order: order,
      status: status.toString(),
      paymentMode: paymentMode,
      scheduleForNextDay: scheduleForNextDay,
    };
    if (paymentMode !== 0) {
    } else {
      temp = {
        ...temp,
        collectedPartially: true,
        collectedCharge: collectedCharge === 0 ? -1 : collectedCharge,
      };
    }
    return temp;
  },
  vehicleOrderSubmitPartialPayment: ({
    order,
    paymentMode,
    collectedCharge,
  }: IVehicleOrderSubmitPartialPayment) => {
    let temp: any = {
      order: order,
      paymentMode: paymentMode,
      collectedPartially: true,
      collectedCharge: collectedCharge,
    };
    return temp;
  },
  addOrder: ({ order }: IRouteAddOrder) => {
    let temp: any = {
      orders: order,
    };
    return temp;
  },
  removeOrder: ({ order }: IRouteAddOrder) => {
    let form = new FormData();
    form.append('order', order);
    return form;
  },
};
